import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, PenLine } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import 'katex/dist/katex.min.css';
import { useDataChannel } from '@livekit/components-react';
import { compileLatex } from './latexCompile';

const EASE_OUT = [0.22, 1, 0.36, 1];

// Retard du tableau sur l'appel d'outil (ms). À régler après test : 0, 400, 800...
const BOARD_DELAY_MS = 0;

/* Légende du code couleur (affichée dès que l'agent utilise une couleur) */
const COLOR_RE = /\\+(ancien|nouveau|operation|attention|fond|textcolor|color)(?![a-zA-Z])/;
const LEGEND = [
  { color: '#e11d48', label: 'remplacé' },
  { color: '#059669', label: 'nouveau' },
  { color: '#2563eb', label: 'opération' },
  { color: '#d97706', label: 'à retenir' }
];

/* Une ligne du tableau : compilée une seule fois, révélée comme si on l'écrivait */
const BoardLine = React.memo(({ line, index, isLast, reduce }) => {
  const compiled = useMemo(() => compileLatex(line.latex), [line.latex]);

  // Opération posée (tableau) : révélée de haut en bas, sinon de gauche à droite.
  // La durée suit la longueur de la ligne, comme une vraie écriture.
  const isBlock = /\\+begin\{/.test(line.latex);
  const len = line.latex.replace(/[\\{}$]/g, '').length;
  const writeDuration = reduce ? 0.01 : isBlock ? 1.6 : Math.min(2.4, 0.5 + len * 0.045);

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      transition={{ duration: 0.25, ease: EASE_OUT }}
      className="overflow-hidden"
    >
      <div className="flex items-start gap-3 py-0.5">
        <span className={`shrink-0 mt-1.5 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border transition-colors duration-300 ${
          isLast ? 'bg-indigo-500 text-white border-indigo-600' : 'bg-white text-slate-400 border-slate-200'
        }`}>
          {index + 1}
        </span>

        <div className="min-w-0 flex-1">
          <div className="relative w-fit max-w-full">
            <motion.div
              initial={{ clipPath: isBlock ? 'inset(0 0 100% 0)' : 'inset(0 100% 0 0)' }}
              animate={{ clipPath: 'inset(0 0 0 0)' }}
              transition={{ duration: writeDuration, ease: 'linear', delay: 0.2 }}
              className={`overflow-x-auto rounded-xl px-3 py-1.5 text-lg text-slate-800 transition-colors duration-300 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
                isLast ? 'bg-indigo-50 ring-1 ring-indigo-200' : ''
              }`}
            >
              <span dangerouslySetInnerHTML={{ __html: compiled.html }} />
            </motion.div>

            {/* Le « stylo » qui suit l'écriture */}
            {!isBlock && !reduce && (
              <motion.span
                aria-hidden="true"
                className="absolute w-0.5 rounded-full bg-indigo-500 pointer-events-none"
                style={{ top: '14%', bottom: '14%' }}
                initial={{ left: '0%', opacity: 1 }}
                animate={{ left: '100%', opacity: [1, 1, 0] }}
                transition={{
                  left: { duration: writeDuration, ease: 'linear', delay: 0.2 },
                  opacity: { duration: writeDuration + 0.3, times: [0, 0.85, 1], delay: 0.2 }
                }}
              />
            )}
          </div>

          {line.note && (
            <motion.p
              initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: writeDuration + 0.2, ease: EASE_OUT }}
              className="px-3 mt-0.5 text-[12px] font-medium text-indigo-500"
            >
              {line.note}
            </motion.p>
          )}
        </div>
      </div>
    </motion.div>
  );
});

/* Tableau blanc alimenté par l'agent via le data channel « board ».
   Commandes : start_board {title} · write_step {id, latex, note} · clear_board
   À placer à l'intérieur de <LiveKitRoom>. */
const Whiteboard = () => {
  const reduce = useReducedMotion();
  const [title, setTitle] = useState('');
  const [lines, setLines] = useState([]);
  const [open, setOpen] = useState(true);
  const [hint, setHint] = useState(false);

  const endRef = useRef(null);
  const timers = useRef([]);
  const hintTimer = useRef(null);
  const activeRef = useRef(false); // le tableau a-t-il déjà du contenu ?

  const showHint = () => {
    setHint(true);
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHint(false), 4500);
  };

  useDataChannel('board', (msg) => {
    let cmd;
    try { cmd = JSON.parse(new TextDecoder().decode(msg.payload)); } catch { return; }
    if (!cmd || typeof cmd !== 'object') return;

    const apply = () => {
      if (cmd.type === 'start_board') {
        setTitle(String(cmd.title || '').replace(/\$/g, ''));
        setLines([]);
        setOpen(true);
        activeRef.current = true;
        showHint();
      } else if (cmd.type === 'write_step') {
        if (!activeRef.current) { activeRef.current = true; showHint(); }
        const line = { id: String(cmd.id), latex: String(cmd.latex || ''), note: cmd.note ? String(cmd.note) : '' };
        setLines((prev) => (prev.some((l) => l.id === line.id) ? prev : [...prev, line]));
        setOpen(true);
      } else if (cmd.type === 'clear_board') {
        setTitle('');
        setLines([]);
        activeRef.current = false;
        setHint(false);
      }
    };
    // même délai pour tous les messages : l'ordre est conservé
    timers.current.push(setTimeout(apply, BOARD_DELAY_MS));
  });

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    clearTimeout(hintTimer.current);
  }, []);

  useEffect(() => {
    const id = setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 120);
    return () => clearTimeout(id);
  }, [lines.length]);

  const usesColors = lines.some((l) => COLOR_RE.test(l.latex));

  if (!title && lines.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -24, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
      className="fixed z-[45] inset-x-3 sm:inset-x-auto sm:right-4 sm:w-[420px]"
      style={{ top: 'calc(env(safe-area-inset-top) + 64px)' }}
    >
      <div className="rounded-3xl bg-white border border-slate-200 shadow-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full px-4 min-h-[44px] py-2 flex items-center justify-between gap-3 bg-slate-900 text-white touch-manipulation"
        >
          <span className="flex items-center gap-2 min-w-0">
            <PenLine className="w-4 h-4 shrink-0 text-indigo-300" />
            <span className="truncate text-[13px] font-bold">{title || 'Tableau'}</span>
          </span>
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? '' : '-rotate-180'}`} />
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
              transition={{ duration: 0.22, ease: EASE_OUT }}
              className="overflow-hidden"
            >
              <div className="max-h-[38vh] overflow-y-auto p-4 space-y-1.5">
                {lines.length === 0 && (
                  <p className="text-[13px] text-slate-400 font-medium motion-safe:animate-pulse">Le professeur écrit…</p>
                )}

                {lines.map((l, i) => (
                  <BoardLine key={l.id} line={l} index={i} isLast={i === lines.length - 1} reduce={reduce} />
                ))}
                <div ref={endRef} />
              </div>

              {usesColors && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="px-4 py-2 border-t border-slate-100 bg-slate-50/70 flex flex-wrap gap-x-3.5 gap-y-1"
                  aria-label="Code couleur"
                >
                  {LEGEND.map((c) => (
                    <span key={c.label} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      <span className="w-2 h-2 rounded-full" style={{ background: c.color }} aria-hidden="true" />
                      {c.label}
                    </span>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bulle d'attention : invite l'élève à regarder le tableau */}
      <AnimatePresence>
        {hint && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 translate-y-full pointer-events-none px-3.5 py-1.5 rounded-full bg-indigo-600 text-white text-[12px] font-bold shadow-lg shadow-indigo-600/30 whitespace-nowrap"
            role="status"
          >
            Regarde le tableau
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Whiteboard;